import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  BrainCircuit, 
  TrendingUp, 
  Target, 
  DollarSign, 
  Zap, 
  Calendar, 
  Filter, 
  Info,
  ArrowRight,
  MousePointer2,
  MessageSquare,
  Clock,
  Sparkles,
  ShieldCheck,
  Repeat,
  Layers
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip 
} from 'recharts';
import './PurchasePredictor.css';

const PurchasePredictor = () => {
  const [activeIntentFilter, setActiveIntentFilter] = useState(null);

  // Data for Intent Distribution Donut
  const intentData = [
    { name: 'High Intent', value: 4201, color: '#F97316', glow: 'rgba(249, 115, 22, 0.4)' },
    { name: 'Medium Intent', value: 12840, color: '#3B82F6', glow: 'rgba(59, 130, 246, 0.4)' },
    { name: 'Low Intent', value: 68167, color: '#64748B', glow: 'rgba(100, 116, 139, 0.4)' },
  ];

  // Data for Forecast Chart
  const forecastData = [
    { name: 'Now', value: 142000, confidence: [135000, 149000] },
    { name: '+30d', value: 185000, confidence: [170000, 200000] },
    { name: '+60d', value: 242000, confidence: [210000, 275000] },
  ];

  const signals = [
    { label: 'Message Sentiment', weight: 85, icon: MessageSquare, color: 'orange' },
    { label: 'Click Recency', weight: 92, icon: MousePointer2, color: 'blue' },
    { label: 'Platform Timing', weight: 45, icon: Clock, color: 'purple' },
    { label: 'Offer Interaction', weight: 78, icon: Zap, color: 'cyan' },
  ];

  const customers = [
    { name: 'Alice Vane', intent: 'High', window: '2-4 Days', ltv: '$1,240', score: 94 },
    { name: 'Thomas Wright', intent: 'High', window: 'Today', ltv: '$850', score: 98 },
    { name: 'Sarah Miller', intent: 'Medium', window: '12 Days', ltv: '$2,100', score: 72 },
    { name: 'James K.', intent: 'Medium', window: 'Unknown', ltv: '$450', score: 65 },
  ];

  const impactSegments = [
    { label: 'VIP Winbacks', value: '84% Odds', trend: 4.2, icon: ShieldCheck, color: 'blue' },
    { label: '60-Day Repeat Rate', subtitle: 'Retention Velocity', value: '24.8%', trend: 3.2, icon: Repeat, color: 'purple', progress: 35 },
    { label: '90-Day LTV', subtitle: 'Avg Order Value', value: '$145.20', trend: 'Flat', icon: Layers, color: 'cyan', progress: 75 },
    { label: 'New Subscriptions', value: '12% Odds', trend: 1.5, icon: DollarSign, color: 'purple' },
  ];

  const totalProfiles = intentData.reduce((sum, item) => sum + item.value, 0);

  return (
    <>
      <Helmet>
        <title>Purchase Predictor - Revexia AI Revenue Brain</title>
        <meta name="description" content="Forecast customer intent and revenue with AI" />
      </Helmet>

      <div className="predictor-page-wrapper">
        <div className="container-fluid px-4">
          {/* View Toolbar */}
          <header className="predictor-toolbar">
            <p className="toolbar-description">
              Forecast customer intent, revenue, and future value with 98.2% accuracy
            </p>
            <div className="toolbar-actions">
              <button className="toolbar-btn">
                <Calendar size={16} />
                <span>60 Days Forecast</span>
              </button>
              <button className="toolbar-icon-btn">
                <Filter size={20} />
              </button>
            </div>
          </header>

          {/* Top Prediction Analytics */}
          <div className="row g-4 mb-5">
            {/* Intent Distribution Donut */}
            <div className="col-12 col-lg-4">
              <div className="intent-distribution-card">
                <div className="intent-card-header">
                  <h3 className="intent-card-title">Intent Spread</h3>
                  <Info size={16} className="intent-info-icon" />
                </div>
                
                <div className="intent-chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={intentData}
                        innerRadius={80}
                        outerRadius={100}
                        paddingAngle={8}
                        dataKey="value"
                        stroke="none"
                        onClick={(data) => setActiveIntentFilter(data.name)}
                      >
                        {intentData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.color}
                            className="intent-pie-cell"
                            style={{ filter: `drop-shadow(0 0 8px ${entry.glow})` }}
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#0D0F29', 
                          border: '1px solid rgba(255,255,255,0.1)', 
                          borderRadius: '12px' 
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="intent-center-label">
                    <span className="intent-total-value">{(totalProfiles / 1000).toFixed(1)}K</span>
                    <span className="intent-total-label">Profiles</span>
                  </div>
                </div>

                <div className="intent-legend-list">
                  {intentData.map((item) => (
                    <div 
                      key={item.name}
                      className={`intent-legend-item ${activeIntentFilter === item.name ? 'active' : ''}`}
                      onClick={() => setActiveIntentFilter(item.name)}
                    >
                      <div className="intent-legend-left">
                        <div 
                          className="intent-legend-dot"
                          style={{ 
                            backgroundColor: item.color, 
                            boxShadow: `0 0 10px ${item.glow}` 
                          }}
                        ></div>
                        <span className="intent-legend-name">{item.name}</span>
                      </div>
                      <span className="intent-legend-percent">
                        {((item.value / totalProfiles) * 100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Predictive Revenue Forecast */}
            <div className="col-12 col-lg-8">
              <div className="revenue-forecast-card">
                <div className="forecast-card-header">
                  <div className="forecast-header-left">
                    <h3 className="forecast-card-title">60-Day Revenue Projection</h3>
                    <p className="forecast-card-subtitle">
                      AI modeling based on current engagement velocity
                    </p>
                  </div>
                  <div className="forecast-growth-badge">
                    <TrendingUp size={16} />
                    <span>+42% Forecasted Growth</span>
                  </div>
                </div>
                
                <div className="forecast-chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={forecastData}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        vertical={false} 
                        stroke="rgba(255,255,255,0.05)" 
                      />
                      <XAxis 
                        dataKey="name" 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} 
                        dy={10} 
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false} 
                        tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} 
                        tickFormatter={(val) => `$${val/1000}k`} 
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(13, 15, 41, 0.95)', 
                          border: '1px solid rgba(255,255,255,0.1)', 
                          borderRadius: '12px', 
                          backdropFilter: 'blur(4px)' 
                        }}
                        itemStyle={{ color: '#fff' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#3B82F6" 
                        strokeWidth={4} 
                        fillOpacity={1} 
                        fill="url(#colorValue)" 
                        animationDuration={2000}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* High-Impact Segments */}
          <section className="impact-segments-section">
            <div className="section-header-with-icon">
              <Target size={20} className="section-icon orange" />
              <h3 className="section-main-title">High-Impact Segments</h3>
            </div>
            <div className="row g-4">
              {impactSegments.map((segment, index) => (
                <div key={index} className="col-12 col-sm-6 col-lg-3">
                  <div className={`impact-segment-card segment-${segment.color}`}>
                    <div className="impact-card-header">
                      <div className="impact-card-icon">
                        <segment.icon size={20} />
                      </div>
                      <div className="impact-card-info">
                        <p className="impact-card-label">{segment.label}</p>
                        {segment.subtitle && (
                          <p className="impact-card-subtitle">{segment.subtitle}</p>
                        )}
                      </div>
                    </div>
                    <div className="impact-card-body">
                      <h4 className="impact-card-value">{segment.value}</h4>
                      {segment.trend !== undefined && segment.trend !== 'Flat' && (
                        <span className="impact-card-trend positive">
                          +{segment.trend}%
                        </span>
                      )}
                      {segment.trend === 'Flat' && (
                        <span className="impact-card-trend neutral">Flat</span>
                      )}
                    </div>
                    {segment.progress && (
                      <div className="impact-progress-track">
                        <div 
                          className={`impact-progress-bar progress-${segment.color}`}
                          style={{ width: `${segment.progress}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Customer Forecasts & Model Drivers */}
          <div className="row g-4 mb-5">
            {/* Individual Forecasts Table */}
            <div className="col-12 col-lg-8">
              <div className="forecasts-table-card">
                <div className="forecasts-table-header">
                  <div className="forecasts-header-left">
                    <h3 className="forecasts-table-title">Individual Forecasts</h3>
                    <p className="forecasts-table-subtitle">
                      Top customers ranked by predictive probability
                    </p>
                  </div>
                  <button className="view-all-btn">View All Profiles</button>
                </div>
                <div className="table-responsive">
                  <table className="forecasts-table">
                    <thead>
                      <tr>
                        <th>Customer / Current Intent</th>
                        <th>Predicted Window</th>
                        <th>Exp. Lifetime Value</th>
                        <th className="text-end">Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customers.map((customer, index) => (
                        <tr key={index} className="forecast-row">
                          <td>
                            <div className="customer-cell">
                              <div className="customer-avatar">
                                <BrainCircuit size={18} />
                              </div>
                              <div className="customer-info">
                                <p className="customer-name">{customer.name}</p>
                                <p className={`customer-intent ${customer.intent.toLowerCase()}`}>
                                  {customer.intent} Intent
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="window-cell">{customer.window}</td>
                          <td className="ltv-cell">{customer.ltv}</td>
                          <td className="confidence-cell">
                            <div className="confidence-wrapper">
                              <span className="confidence-score">{customer.score}%</span>
                              <div className="confidence-bar-track">
                                <div 
                                  className="confidence-bar-fill"
                                  style={{ width: `${customer.score}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Model Drivers */}
            <div className="col-12 col-lg-4">
              <div className="model-drivers-card">
                <div className="drivers-card-header">
                  <div className="drivers-icon-wrapper">
                    <Sparkles size={20} />
                  </div>
                  <h3 className="drivers-card-title">Model Drivers</h3>
                </div>
                <p className="drivers-card-description">
                  Real-time weights assigned to behavioral signals influencing the prediction engine.
                </p>

                <div className="drivers-list">
                  {signals.map((signal, index) => (
                    <div key={index} className="driver-item">
                      <div className="driver-item-header">
                        <div className="driver-item-left">
                          <div className="driver-icon">
                            <signal.icon size={14} />
                          </div>
                          <span className="driver-label">{signal.label}</span>
                        </div>
                        <span className="driver-weight">{signal.weight}%</span>
                      </div>
                      <div className="driver-progress-track">
                        <div 
                          className={`driver-progress-bar progress-${signal.color}`}
                          style={{ width: `${signal.weight}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="explainable-ai-box">
                  <p className="explainable-ai-label">Explainable AI Node</p>
                  <p className="explainable-ai-text">
                    "Message engagement has risen to a 0.85 weight coefficient, indicating conversation quality is currently the strongest buyer signal."
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Panel - Fixed Bottom */}
          <div className="action-panel-fixed">
            <div className="action-panel-content">
              <div className="action-panel-left">
                <div className="action-panel-icon">
                  <Zap size={20} />
                </div>
                <div className="action-panel-info">
                  <p className="action-panel-label">Recommended Action</p>
                  <p className="action-panel-title">
                    Push "Flash Offer" to 4.2K High-Intent Users
                  </p>
                </div>
              </div>
              <div className="action-panel-divider"></div>
              <div className="action-panel-buttons">
                <button className="simulate-btn">Simulate Impact</button>
                <button className="apply-btn">
                  <span>Apply Strategy</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchasePredictor;
